/**
 * Per-code detail loader with IndexedDB caching.
 *
 * Fetches per-code JSON files from R2 CDN (~5-15KB each),
 * caches in IndexedDB with 48h TTL for instant subsequent loads.
 *
 * Usage:
 *   const detail = await loadCodeDetail('cpt', '99291');
 *   // detail includes pricing stats, hospital lists, LLM summary, etc.
 */

const CDN_BASE = 'https://static.americanhealthprices.com/data';

const DB_NAME = 'ahp-code-detail-v1';
const DB_VERSION = 1;
const STORE_NAME = 'codes';
const STALE_MS = 48 * 60 * 60 * 1000; // 48 hours

export interface CodeDetailSettingBreakdown {
  setting: string;
  avgCharge: number | null;
  medianCharge: number | null;
  hospitalCount: number;
}

/** Full per-bucket detail (setting × unit × quantity) — used by drilldown UI */
export interface CodeDetailBucket {
  setting: string;
  unit: string;
  unitQuantity: number;
  avgCharge: number | null;
  medianCharge: number | null;
  minCharge: number | null;
  maxCharge: number | null;
  hospitalCount: number;
}

export interface CodeDetailHospital {
  id: string;
  n: string;  // name
  c: string;  // city
  s: string;  // state
  p: number;  // price
  st: string; // setting (inpatient/outpatient)
  la: number | null;
  lo: number | null;
}

export interface CodeDetailInternational {
  countryCode: string;
  countryName: string;
  flag: string;
  priceUSD: number;
  source: string;
}

export interface CodeDetailPricing {
  avgGrossCharge: number | null;
  medianGrossCharge: number | null;
  minGrossCharge: number | null;
  maxGrossCharge: number | null;
  totalHospitals: number;
  priceVariation: number | null;
  bySettings: CodeDetailSettingBreakdown[];
  /** Full per-bucket breakdown (setting × unit × quantity). Optional — only present for code types
   *  that have unit dimensions (cpt, hcpcs, rev, ndc). DRG and others fall back to bySettings. */
  buckets?: CodeDetailBucket[];
}

export interface CodeDetail {
  code: string;
  codeType: string;
  description: string;
  consumerName: string;
  llmSummary: string;
  educationalHtml: string;
  pricing: CodeDetailPricing;
  hospitals?: CodeDetailHospital[];  // Full hospital list (new format)
  cheapestHospitals?: CodeDetailHospital[];  // Legacy (pre-v2)
  mostExpensiveHospitals?: CodeDetailHospital[];  // Legacy (pre-v2)
  internationalPricing?: CodeDetailInternational[];
  genericName?: string;
  brandName?: string;
}

interface CachedEntry {
  generated_at: string;
  v: number;
  data: CodeDetail;
  cached_at: number;
}

// ── IndexedDB helpers ─────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getFromIDB(key: string): Promise<CachedEntry | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function putToIDB(key: string, data: CachedEntry): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IndexedDB write failure is non-fatal
  }
}

// ── In-memory cache + deduplication ───────────────────────────────────────

const memoryCache = new Map<string, CodeDetail>();
const inflightRequests = new Map<string, Promise<CodeDetail | null>>();

/**
 * Load a code detail from R2 CDN.
 * Returns full code data (pricing, hospitals, summary) or null if not found.
 * Caches in IndexedDB for 48h and in memory for the session.
 *
 * @param codeType - 'cpt', 'hcpcs', 'drg', 'rev', or 'ndc'
 * @param code - The procedure code (e.g., '99291', 'J2795')
 */
export async function loadCodeDetail(codeType: string, code: string): Promise<CodeDetail | null> {
  if (!codeType || !code) return null;

  const cacheKey = `${codeType}:${code}`;

  // 1. Memory cache (instant)
  const mem = memoryCache.get(cacheKey);
  if (mem) return mem;

  // 2. Deduplicate concurrent fetches
  const inflight = inflightRequests.get(cacheKey);
  if (inflight) return inflight;

  const promise = _fetchCodeDetail(codeType, code);
  inflightRequests.set(cacheKey, promise);

  try {
    return await promise;
  } finally {
    inflightRequests.delete(cacheKey);
  }
}

/**
 * Normalize a code for storage lookup. DRG JSON files on R2 are keyed with the `MS-` prefix
 * (e.g. `data/codes/drg/MS-470.json`) per the generator's naming convention. URLs in the
 * wild come in both forms — `/drg/470` and `/drg/MS-470`. Add the prefix when missing so
 * either route resolves to the same R2 object.
 */
function normalizeCodeForLookup(codeType: string, code: string): string {
  if (codeType !== 'drg') return code;
  if (/^MS-/i.test(code)) return code.toUpperCase();
  // Bare numeric DRGs (e.g. "470", "0470", "9") → MS-470, MS-470, MS-009
  const trimmed = code.replace(/^0+/, '') || '0';
  return `MS-${trimmed.padStart(3, '0')}`;
}

async function _fetchCodeDetail(codeType: string, code: string): Promise<CodeDetail | null> {
  const lookupCode = normalizeCodeForLookup(codeType, code);
  const cacheKey = `${codeType}:${lookupCode}`;

  // 1. Check IndexedDB
  const idbKey = `code-detail:${cacheKey}`;
  const cached = await getFromIDB(idbKey);
  if (cached && cached.data && (Date.now() - cached.cached_at) < STALE_MS) {
    memoryCache.set(cacheKey, cached.data);
    return cached.data;
  }

  // 2. Fetch from R2 CDN
  try {
    const safeCode = lookupCode.replace(/\//g, '_');
    const resp = await fetch(`${CDN_BASE}/codes/${codeType}/${safeCode}.json`);
    if (!resp.ok) {
      if (resp.status === 404) return null; // Code not in static cache
      console.warn(`[CodeDetail] Fetch failed for ${cacheKey}:`, resp.status);
      return null;
    }

    const json = await resp.json();
    const detail: CodeDetail = json.data;

    if (!detail || !detail.code) return null;

    // Cache in memory + IDB
    memoryCache.set(cacheKey, detail);
    putToIDB(idbKey, {
      generated_at: json.generated_at,
      v: json.v || 1,
      data: detail,
      cached_at: Date.now(),
    });

    return detail;
  } catch (e) {
    console.warn(`[CodeDetail] Error fetching ${cacheKey}:`, e);
    return null;
  }
}
